import { RailgunErrorMessage } from 'src/app/RailgunError';
import { AppViewReceiverConst } from 'src/app/Util/AppReceiver/AppViewReceiverConst';
import { AppViewReceiverModel } from 'src/app/Util/AppReceiver/AppViewReceiverModel';
import { AppViewReceiverUtil } from 'src/app/Util/AppReceiver/AppViewReceiverUtil';
import { PopoverAlertComponent } from '../../Common/Popover/Alert/PopoverAlertComponent';
import { BaseView } from './BaseView';
import { StorageService } from "../../../Service/StorageService";

/**
 * BaseViewUI
 * 各UIが継承する為のラッパークラス。循環参照回避用。
 * ここで使いたいViewは、BaseViewの方を継承する事。
 */
export abstract class BaseViewUI extends BaseView {
	/**
	 * ユーザー側のスピナーとエラー処理をパックにした実行
	 * @param view		実行元のインスタンス(this)
	 * @param func 		実行する処理
	 * @param paramObj 	実行する処理に渡す引数をパックにしたオブジェクト
	 */
	public async execUserFuncWithErrorHandling(view: BaseViewUI, func: any, paramObj: any, showNotFound = false) {
		// 指定Viewがない場合は終わり
		if (!view) {
			return;
		}

		// スピナー表示
		await view.showSpinnerMainView(view);

		if (!func || !paramObj) {
			this.log('----------------execUserFuncWithErrorHandling on erro. func or paramObj is null.-----------------');
			this.log(func);
			this.log(paramObj);
		}

		// 処理実行
		const result = func && paramObj ? await func(paramObj) : null;

		// スピナー表示を消す
		await view.closeSpinnerMainView(view);

		// エラー表示。スピナーの戻しの後でないと、スピナーのクローズに巻き込まれて消えちゃう。
		const errObj = await view.getErrorObj();
		if (errObj) {
			this.log('----------------execUserFuncWithErrorHandling on error-----------------');
			this.log(errObj);

			if (errObj.getHttpStatus() === 401) {
				const svc = new StorageService(this);
				await svc.clear();
				location.reload();
			}

			// 404のデータ無しは表示しないオプションONなら
			if (!showNotFound && errObj.dataNotFound) {
				view.setErrorObj(null); // エラーオブジェクトをクリア
				return result;
			}

			// サーバのバリデーションエラーなら、サーバ側のエラーメッセージを出して終わり
			if (errObj.saverValidationError) {
				await view.showAlertDialog('エラー', errObj.getMessage());
				view.setErrorObj(null); // エラーオブジェクトをクリア
				return result;
			}

			// エラーメッセージを出して再表示
			await this.showDialogWindow(PopoverAlertComponent, {
				title: 'エラー',
				text: RailgunErrorMessage.ERR__MESSAGE_RELOAD,
				close: () => {
					this.log('----------------reload func-----------------');
					// エラーなので、強制リロードにする
					location.reload();
				},
			});

			view.setErrorObj(null); // エラーオブジェクトをクリア
		}

		return result;
	}

	/**
	 * スピナーとエラー処理をパックにした実行
	 * @param view		実行元のインスタンス(this)
	 * @param func 		実行する処理
	 * @param paramObj 	実行する処理に渡す引数をパックにしたオブジェクト
	 * @param showNotFound 404 をエラーとするか
	 */
	public async execFuncWithErrorHandling(view: BaseViewUI, func: any, paramObj: any, showNotFound = true) {
		// 指定Viewがない場合は終わり
		if (!view) {
			return;
		}

		// スピナー表示
		await view.showSpinnerMainView(view);

		if (!func || !paramObj) {
			this.log('----------------execFuncWithErrorHandling on erro. func or paramObj is null.-----------------');
			this.log(func);
			this.log(paramObj);
		}

		// 処理実行
		const result = func && paramObj ? await func(paramObj) : null;

		// スピナー表示を消す
		await view.closeSpinnerMainView(view);

		// エラー表示。スピナーの戻しの後でないと、スピナーのクローズに巻き込まれて消えちゃう。
		const errObj = await view.getErrorObj();
		if (errObj) {
			this.log('----------------execFuncWithErrorHandling on error-----------------');
			this.log(errObj);

			if (errObj.getHttpStatus() === 401) {
				const svc = new StorageService(this);
				await svc.clear();
				location.reload();
			}

			// 404のデータ無しは表示しないオプションONなら
			if (!showNotFound && errObj.dataNotFound) {
				await view.setErrorObj(null); // エラーオブジェクトをクリア
				return result;
			}

			// applicationエラーの場合
			const err = errObj.respBody as any;
			if (err.err === 'application') {
				await view.showAlertDialog('エラー', err.message);
				await view.setErrorObj(null); // エラーオブジェクトをクリア
				return null;
			}

			const msg = errObj ? errObj.getMessage() : RailgunErrorMessage.ERR__MESSAGE_OTHER;
			await view.showAlertDialog('エラー', msg);

			await view.setErrorObj(null); // エラーオブジェクトをクリア
			return null;
		}

		return result;
	}

	// BaseView領域以下は、必ずMainViewのインスタンスが存在する前提なので注意。
	/**
	 * showSpinnerMainView
	 * Wait用スピナーの表示
	 * @param callInst 呼び出したクラスのインスタンス。this想定。
	 */
	public showSpinnerMainView(callInst: any) {
		// スピナーの表示イベントの発行
		const targetDomain = AppViewReceiverConst.General.View;
		const targetName = targetDomain.AppMainViewComponent;
		const eventTargetModel = new AppViewReceiverModel(targetDomain, targetName, targetName.ShowSpinner, callInst);
		AppViewReceiverUtil.execEventProc(eventTargetModel, null);
	}

	/**
	 * closeSpinnerMainView
	 * showSpinnerMainView
	 * Wait用スピナーを消す
	 * @param callInst 呼び出したクラスのインスタンス。this想定。
	 */
	public closeSpinnerMainView(callInst: any) {
		// スピナーの非表示イベントの発行
		const targetDomain = AppViewReceiverConst.General.View;
		const targetName = targetDomain.AppMainViewComponent;
		const eventTargetModel = new AppViewReceiverModel(targetDomain, targetName, targetName.CloseSpinner, callInst);
		AppViewReceiverUtil.execEventProc(eventTargetModel, null);
	}

	/**
	 * showAlertDialog
	 * アラートを表示する
	 * @param title タイトル
	 * @param text	本文
	 */
	public showAlertDialog(title: string, text: string) {
		// エラーダイアログを表示する。
		this.showDialogWindow(PopoverAlertComponent, {
			title,
			text,
			close: this.dismissDialogWindow.bind(this),
		});
	}
}
